import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { ActivityIndicator, Platform, View } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import {
  Button,
  FadeView,
  IconFavoriteFill,
  IconFavoriteOutline,
  IconInfo,
  Pressable,
  Text,
  tokens,
} from '@fhs/ui';
import { useIsDesktopBp } from '@fhs-legacy/frontend/src/hooks/breakpoints';
import { STORE_LOCATOR_STORE_LIST_ORDER_BUTTON } from '@fhs-legacy/frontend/src/utils/test-ids';

import { isKeyboardFocus } from '../../utils/is-keyboard-focus';

import { StoreCardFrame } from './components/store-card-frame';
import { StoreCardSelectRegion } from './components/store-card-select-region';
import { useStoreCardMqStyles } from './store-card.styles';
import type { StoreCardV2ViewProps } from './types';

// Built once so the entering preset isn't recreated on every card render.
const storeCardEntering = FadeIn;

/**
 * Native uses Reanimated's FadeIn. Web uses FadeView: the metro transformer strips
 * react-native-reanimated from the web bundle. The animated view wraps the pressable: on the
 * New Architecture a pressable overwrites `nativeID` on its children, which stops an entering
 * animation placed inside it from running.
 */
const StoreCardEntering = Platform.select<React.FC<{ children: React.ReactNode }>>({
  native: ({ children }) => <Animated.View entering={storeCardEntering}>{children}</Animated.View>,
  web: ({ children }) => <FadeView>{children}</FadeView>,
})!;

export const StoreCardV2View: React.VFC<StoreCardV2ViewProps> = ({
  accessibilityLabel,
  address,
  badge,
  ctaAccessibilityLabel,
  ctaLabel,
  distanceText,
  hoursText,
  isCtaDisabled,
  isCtaLoading,
  isCtaBlocked = false,
  isFavorite,
  isSelected,
  isStoreLocatorFocusTarget,
  isUpdatingFavorites,
  name,
  onCtaPress,
  onFavoritePress,
  onInfoPress,
  onSelect,
  statusText,
  storeLocatorFocusRef,
  testID,
}) => {
  const { formatMessage } = useIntl();
  const styles = useStoreCardMqStyles();
  const isDesktop = useIsDesktopBp();
  // A focus ring around the heart reads as "still favourited" after a click to unfavourite.
  const [showFavoriteFocusRing, setShowFavoriteFocusRing] = useState(true);

  const iconSize = isDesktop ? 22 : 20;
  const storeName = name ?? '';
  const favoriteLabel = isFavorite
    ? formatMessage({ id: 'removeFavorite' })
    : formatMessage({ id: 'setRestaurantAsFavorite' });
  const infoLabel = formatMessage({ id: 'storeInfoAssistive' }, { storeLabel: storeName });

  return (
    <StoreCardEntering>
      <StoreCardFrame
        testID={testID}
        onPress={onSelect}
        style={[styles.card, isSelected && styles.cardSelected]}
        hoveredStyle={isSelected ? undefined : styles.cardHovered}
      >
        <View style={styles.cardBody}>
          {badge ? (
            <View
              style={[
                styles.badge,
                badge.tone === 'error' ? styles.badgeError : styles.badgeWarning,
              ]}
            >
              <Text.Ui
                size="sm"
                weight="bold"
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
                style={badge.tone === 'error' ? styles.badgeErrorText : styles.badgeWarningText}
              >
                {badge.label}
              </Text.Ui>
            </View>
          ) : null}

          <View style={styles.bodyRow}>
            <View style={styles.selectRegion}>
              <StoreCardSelectRegion
                accessibilityLabel={accessibilityLabel}
                isSelected={isSelected}
                onPress={onSelect}
                isStoreLocatorFocusTarget={isStoreLocatorFocusTarget}
                storeLocatorFocusRef={storeLocatorFocusRef}
                dd-action-name={`${storeName} select store`}
              >
                <View style={styles.selectRegionContent}>
                  <Text.Ui size="md" weight="semibold">
                    {storeName}
                    {distanceText ? ' • ' : null}
                    {distanceText ? <Text.Ui size="xs">{distanceText}</Text.Ui> : null}
                  </Text.Ui>

                  {address ? (
                    // One text node so the street and city share a line break, without the
                    // gap the surrounding column puts between separate children.
                    <Text.Ui size="sm">{address}</Text.Ui>
                  ) : null}

                  <View style={styles.statusRow}>
                    <Text.Ui size="xs" weight="semibold">
                      {statusText}
                    </Text.Ui>
                    {hoursText ? (
                      <>
                        <Text.Ui size="xs" weight="bold">
                          •
                        </Text.Ui>
                        <Text.Ui size="xs">{hoursText}</Text.Ui>
                      </>
                    ) : null}
                  </View>
                </View>
              </StoreCardSelectRegion>
            </View>

            <View style={styles.ctaColumn}>
              <View style={styles.iconRow}>
                <Pressable
                  accessibilityLabel={infoLabel}
                  dd-action-name={`${storeName} store info`}
                  onPress={onInfoPress}
                  hitSlop={8}
                >
                  <IconInfo size={iconSize} color={tokens.colors.$blackOpacity75} />
                </Pressable>

                <Pressable
                  accessibilityLabel={favoriteLabel}
                  accessibilityState={{ busy: isUpdatingFavorites, selected: isFavorite }}
                  dd-action-name={`${storeName} ${isFavorite ? 'unfavorite' : 'favorite'}`}
                  disabled={isUpdatingFavorites}
                  onPress={onFavoritePress}
                  onFocus={event => setShowFavoriteFocusRing(isKeyboardFocus(event.target))}
                  outlineFocusedStyle={
                    showFavoriteFocusRing ? undefined : styles.favoriteOutlineHidden
                  }
                  hitSlop={8}
                >
                  <View style={styles.favoriteSlot}>
                    <View
                      accessibilityElementsHidden={isUpdatingFavorites}
                      importantForAccessibility={
                        isUpdatingFavorites ? 'no-hide-descendants' : 'auto'
                      }
                      style={isUpdatingFavorites ? styles.favoriteIconHidden : undefined}
                    >
                      {isFavorite ? (
                        <IconFavoriteFill size={iconSize} color={tokens.colors.$houseRed} />
                      ) : (
                        <IconFavoriteOutline
                          size={iconSize}
                          color={tokens.colors.$blackOpacity75}
                        />
                      )}
                    </View>
                    {isUpdatingFavorites ? (
                      <View style={styles.favoriteSpinner} pointerEvents="none">
                        <ActivityIndicator size="small" color={tokens.colors.$houseRed} />
                      </View>
                    ) : null}
                  </View>
                </Pressable>
              </View>

              {ctaLabel ? (
                <Button
                  testID={STORE_LOCATOR_STORE_LIST_ORDER_BUTTON}
                  dd-action-name={`${storeName} ${ctaLabel}`}
                  // 31 tall on mobile, 34 on desktop, matching the two button sizes in the design.
                  size={isDesktop ? 'sm' : 'xs'}
                  type={isCtaDisabled ? 'outline' : 'solid'}
                  accessibilityLabel={ctaAccessibilityLabel}
                  accessibilityState={isCtaBlocked ? { disabled: true } : undefined}
                  disabled={isCtaDisabled || isCtaLoading}
                  // `disabled` would restyle the button grey, so a blocked CTA ignores pointer
                  // input and drops its press handler (keyboard) instead.
                  style={isCtaBlocked ? styles.ctaBlocked : undefined}
                  loading={isCtaLoading}
                  onPress={isCtaBlocked ? undefined : onCtaPress}
                >
                  <Button.Text>{ctaLabel}</Button.Text>
                </Button>
              ) : null}
            </View>
          </View>
        </View>
        <View style={styles.divider} />
      </StoreCardFrame>
    </StoreCardEntering>
  );
};
